import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { getDateWithLongMonth } from "src/utils/Date";
import { Route, selectRoute } from "src/config/routes";
import { UI } from "@wwimmo/ui";
import styles from "./Protocol.module.css";
import { useParams, useHistory } from "react-router";
import { runInAction } from "mobx";
import { ProtocolPreview } from "./preview/ProtocolPreview";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";

interface Params {
    assemblyid?: string;
}

type AssemblyField = "protocolauthor" | "protocoldate";

const ProtocolBase = () => {
    const { t } = useTranslation();
    const { assemblyid } = useParams<Params>();
    const { navStore, assemblyStore, authStore, protocolStore } = useContext(RootStoreContext);
    const history = useHistory();

    useEffect(() => {
        if (assemblyid) {
            if (!assemblyStore.currentAssembly) {
                assemblyStore.loadCurrentAssembly(assemblyid).then(() => {
                    protocolStore.setInitialProtocol();
                });
            } else {
                // Even if currentAssembly exists, capture its initial protocol
                protocolStore.setInitialProtocol();
            }
        }
        if (assemblyStore.currentAssembly) {
            navStore.setTitle(
                `${assemblyStore.currentAssembly.title} am ${getDateWithLongMonth(
                    assemblyStore.currentAssembly.date || ""
                )}` || ""
            );
        }

        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.assemblies, authStore.user?.role),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [assemblyStore, authStore, navStore, t, assemblyStore.currentAssembly, assemblyid, protocolStore]);

    useEffect(() => {
        if (assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.LIVEMODE_ACTIVE) {
            history.push(
                selectRoute(Route.assemblyLiveMode, authStore.user?.role, {
                    assemblyid: assemblyStore.currentAssembly?.id || ""
                })
            );
        }
    }, [assemblyStore.currentAssembly?.state, history, authStore.user?.role, assemblyStore.currentAssembly?.id]);

    const onSaveAssemblyProtocol = useCallback(
        (e: any) => {
            e.preventDefault();
            protocolStore.saveAssemblyProtocol();
        },
        [protocolStore]
    );

    const onDiscardAssemblyProtocol = useCallback(
        (e: any) => {
            e.preventDefault();
            protocolStore.discardAssemblyProtocol();
        },
        [protocolStore]
    );

    const onDownloadAssemblyProtocol = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            protocolStore.checkAndDownloadAssemblyProtocolPdf(assemblyid || "");
        },
        [protocolStore, assemblyid]
    );

    const handleAssemblyFieldChange = useCallback(
        (field: AssemblyField) => (e: React.ChangeEvent<HTMLInputElement>) => {
            runInAction(() => {
                if (assemblyStore.currentAssembly) {
                    assemblyStore.currentAssembly[field] = e.target.value;
                }
            });
        },
        [assemblyStore]
    );

    const formatDateForInput = (date: string) => {
        const d = new Date(date);
        if (isNaN(d.getTime())) {
            return "";
        }
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const protocoldateValue = assemblyStore.currentAssembly?.protocoldate
        ? formatDateForInput(assemblyStore.currentAssembly.protocoldate)
        : "";

    return (
        <UI.Container className="pt-4">
            <UI.Card title={t("screens.assemblies.protocolcontent.declarations").toString()}>
                <div className={styles.InputRow}>
                    <UI.Input
                        className={styles.InputProtocolauthor}
                        label={`${t("screens.assemblies.protocolcontent.protocolauthor")} *`}
                        value={assemblyStore.currentAssembly?.protocolauthor || ""}
                        onChange={handleAssemblyFieldChange("protocolauthor")}
                    />
                    <div className="mb-3">
                        <label className="form-label" htmlFor="date">{`${t(
                            "screens.assemblies.protocolcontent.protocoldate"
                        )} *`}</label>
                        <div className="input-group">
                            <input
                                className={styles.InputDate}
                                type="date"
                                id="protocoldate"
                                value={protocoldateValue}
                                onChange={handleAssemblyFieldChange("protocoldate")}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.ButtonRow}>
                    <UI.Button
                        className={styles.ButtonSave}
                        label={t("labels.save").toString()}
                        onClick={onSaveAssemblyProtocol}
                        disabled={!protocolStore.protocolHasChanged}
                    />
                    <UI.Button
                        className={styles.ButtonDiscard}
                        label={t("labels.discard").toString()}
                        onClick={onDiscardAssemblyProtocol}
                        disabled={!protocolStore.protocolHasChanged}
                        variant="outline-primary"
                    />
                    {protocolStore.isProtocolPDFDownloading ? (
                        <UI.RotatingSpinner noLogo={true} size={25} />
                    ) : (
                        <UI.Button
                            className={styles.ButtonDiscard}
                            label={t("labels.download").toString()}
                            onClick={onDownloadAssemblyProtocol}
                            variant="outline-primary"
                        />
                    )}
                </div>
            </UI.Card>
            <ProtocolPreview assemblyId={assemblyid || ""} />
        </UI.Container>
    );
};

export const Protocol = Sentry.withProfiler(observer(ProtocolBase));
